import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import { useSegment } from 'common/hooks/segment';
import { Routes } from 'constants/pages';
import { checkCode } from 'services/clients/user';
import { Resend } from './Resend';
import { useCheckOnMount } from '../hooks';

/**
 * Renders the component for inputting a verification code
 */
export const CodeForm: React.FC = () => {
  /**
   * Code: the currently entered data
   * failedCode: the code that returned an error response for use as a placeholder
   */

  const [params] = useSearchParams();
  const email = params.get('email');
  const codeParam = params.get('code');
  const [state, setState] = React.useState<{ code: string; failedCode: string }>({
    code: codeParam || '',
    failedCode: '',
  });

  const navigate = useNavigate();

  const { track } = useSegment();

  const mutation = useMutation(checkCode, { mutationKey: ['checkCode'] });

  // Upon mount, make sure we send the email to the user or continue if they're already validated
  useCheckOnMount(email);

  // When the code changes, we trigger a mutation if it is 6 long
  React.useEffect(() => {
    const { code } = state;
    if (state.code.length === 6 && email) {
      mutation.mutate(
        { email, code },
        {
          onError: (_, values) => {
            setState({ code: '', failedCode: values.code });
            track('User submitted email verification code', {
              email,
              success: false,
              usedCode: code,
            });
          },
          onSuccess: () => {
            navigate(`${Routes.SignUp}?email=${encodeURIComponent(email)}`);
            track('User submitted email verification code', {
              email,
              success: true,
              usedCode: code,
            });
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.code, setState]);

  const handleCodeChange = (code: string) => {
    setState({ code, failedCode: '' });
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-center">
        <div className="absolute w-[10px] border-t" />
        <OtpInput
          key={state.failedCode}
          numInputs={6}
          value={state.code}
          placeholder={state.failedCode}
          onChange={handleCodeChange}
          inputStyle={{
            textAlign: 'center',
            width: '100%',
            height: '100%',
            background: 'transparent',
          }}
          shouldAutoFocus
          className="bg-white border border-gray-400 border-r-0 w-10 h-14 sm:w-16 sm:h-24 md:w-[79px] md:h-[94px]"
          containerStyle="
            everyFirstThirdChild:rounded-l
            everyLastThirdChild:rounded-r
            everyFirstThirdChild:ml-4
            everyLastThirdChild:mr-4
            everyLastThirdChild:border-r
            input:text-2xl
            inputPlaceholder:text-2xl
          "
        />
      </div>
      <div className="text-left ml-4">
        {mutation.isLoading && <p>Checking code…</p>}
        {mutation.isError && <p className="text-red-900">Wrong code. Please try again.</p>}
        <div className="h-4 pt-12">{email && <Resend email={email} />}</div>
      </div>
    </div>
  );
};
